import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, FormLabel, Stack, Button, Grid } from '@mui/material';
import { ClientDetailField } from '@/components/ClientDetailEntry/ClientDetailField';
import { useUpdateClientxMutation } from '@/features/clients/clientsApi';
import { addressSubtitles, clientHeaders } from '@/components/ClientDetailEntry/clientDetailData';
import { ClientCategory } from '@/components/cards/DealCardValues/ClientCategory';
import { GooglePlacesAutocomplete } from '@/components/selects/GooglePlacesAutocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { OldAddContactDialog } from '@/components/dialogs/AddContactDialog';
import { useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import UpdateClientForm from '@/components/forms/UpdateClientForm';
import { showToast } from '@/features/toast/toastSlice';
import ContactDetails from './ContactDetails';

const ClientDetailsTab = () => {
	const { getFeatureFlags } = useFeatureFlagContext();
	const useNewCreateClientForm = Boolean(getFeatureFlags('createNewClient')?.active);

	return useNewCreateClientForm ? <UpdateClientForm /> : <DetailsTab />;
};
ClientDetailsTab.displayName = 'ClientDetailsTab';

const DetailsTab = () => {
	const { clientId } = useParams();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const showAddressee = userDetails?.broker?.ledger?.startsWith('ausure');

	const dispatch = useDispatch();

	const [updateClientx, clientInsightState] = useUpdateClientxMutation();

	const loading = clientInsightState.isLoading;

	const { getFeatureFlags } = useFeatureFlagContext();
	const showClientStatus = Boolean(getFeatureFlags('clientStatusFilter')?.active);

	const [isEditMode, setIsEditMode] = useState(false);

	const handleUpdateClient = useCallback(
		async (address, city, suburb, country, postcode, stateValue) => {
			try {
				const insightResponse = await updateClientx({
					clientId,
					insight: {
						AddressLine1: address,
						Suburb: suburb,
						City: city,
						Postcode: postcode,
						Country: country,
						StateId: stateValue,
					},
					firebase: {},
				}).unwrap();
				if (insightResponse.error) {
					console.log('insight error: ', insightResponse.error);
					return;
				}
			} catch (error) {
				console.error('Error updating client address:', error);
				dispatch(
					showToast({
						message: `Error updating client: ${error.data?.message || error.message}`,
						error: true,
					}),
				);
			}
		},
		[clientId, updateClientx, dispatch],
	);

	const handleAddressSelect = useCallback(
		async (place) => {
			const isAustralian = userDetails?.broker?.country === 'AU';

			console.log('Selected address', place);

			const address = place.addressLine1 ?? '';
			const suburb = (isAustralian ? place.city : place.suburb) ?? '';
			const city = isAustralian ? '' : (place.city ?? '');
			const stateValue = isAustralian ? (place.state ?? '') : '';
			const postcode = place.postcode ?? '';
			const country = place.country ?? '';

			await handleUpdateClient(address, city, suburb, country, postcode, stateValue);
		},
		[userDetails?.broker?.country, handleUpdateClient],
	);

	const clientHeadersFinal = useMemo(() => {
		const result = {};
		Object.entries(clientHeaders).forEach(([key, value]) => {
			if (key === 'isCurrent' && !showClientStatus) return;
			result[key] = value;
			if (key === 'industry') {
				result.standardIndustryCode = 'Standard Industry Code';
			}
			if (showAddressee && key === 'tradingAs') result.Addressee = 'Addressee';
		});
		return result;
	}, [showAddressee, showClientStatus]);

	const ClientSectionComponent = useMemo(() => {
		const Component = () => (
			<Box
				mt={5}
				style={{
					marginBottom: '18px',
					padding: '12px',
				}}
			>
				<Stack direction={'row'} alignItems={'center'}>
					<Typography variant='details_header' width={'100%'}>
						Client Details
					</Typography>
					<Box display='flex' justifyContent='flex-end' alignItems='center' width='100%'>
						<Stack spacing={1} direction='row' alignItems='center'>
							<Button
								onClick={() => setIsEditMode(!isEditMode)}
								sx={(theme) => ({
									color: !isEditMode ? theme.palette.primary.main : 'inherit',
								})}
								startIcon={!isEditMode ? <EditIcon /> : <CloseIcon />}
							>
								{!isEditMode ? 'Edit' : 'Close'}
							</Button>
						</Stack>
					</Box>
				</Stack>
				<Box sx={{ mt: 2 }}>
					<ClientCategory disabled={!isEditMode} />
				</Box>
				{Object.entries(clientHeadersFinal).map(([key, header]) => (
					<Grid container padding={'1em 0 0.5em'} key={`detail-entry-${key}`}>
						<Grid size={12}>
							{key === 'address' ? (
								<Grid container columns={{ xs: 1, sm: 2 }} spacing={1}>
									<Grid size={12} key='google-places-autocomplete'>
										<div>
											<FormLabel
												component='legend'
												style={{
													fontSize: '0.875rem',
													fontWeight: 'semibold',
													marginBottom: '0.2rem',
												}}
											>
												Find and update Address
											</FormLabel>
											<GooglePlacesAutocomplete
												disabled={!isEditMode}
												onSelectAddress={handleAddressSelect}
											/>
										</div>
									</Grid>
									{Object.entries(addressSubtitles)
										.filter(([k]) => {
											if (k === 'StateName')
												return userDetails?.broker?.country === 'AU';
											if (k === 'city')
												return userDetails?.broker?.country !== 'AU';
											return true;
										})
										.map(([k, h]) => (
											<Grid size={1} key={`detail-entry-address-${k}`}>
												<ClientDetailField
													header={k}
													title={h}
													disabled={!isEditMode}
												/>
											</Grid>
										))}
								</Grid>
							) : (
								<ClientDetailField
									header={key}
									title={header}
									disabled={!isEditMode}
								/>
							)}
						</Grid>
					</Grid>
				))}
			</Box>
		);

		Component.displayName = 'ClientSection';
		return Component;
	}, [isEditMode, clientHeadersFinal, handleAddressSelect, userDetails?.broker?.country]);

	return (
		<>
			<Box>
				<ContactDetails />
				<ClientSectionComponent />
			</Box>

			<OldAddContactDialog />
		</>
	);
};
DetailsTab.displayName = 'DetailsTab';

export default ClientDetailsTab;
